import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClubMembersListParams, ClubsListParams } from "../../../admin-api/clubs";
import { adminApi } from "../../../api-client";
import type { UpdateBoost, UpdateClub, UpdateSubscription } from "../../../types";
import { queryKeys } from "../../query-keys";

export function useClubs(params: ClubsListParams = {}) {
	return useQuery({
		queryKey: [...queryKeys.clubs.all, "list", params] as const,
		queryFn: () => adminApi.clubs.list(params),
		placeholderData: keepPreviousData,
	});
}

export function useClub(id: string) {
	return useQuery({
		queryKey: queryKeys.clubs.detail(id),
		queryFn: () => adminApi.clubs.get(id),
	});
}

export function useClubMembers(clubId: string) {
	return useQuery({
		queryKey: queryKeys.clubs.members(clubId),
		queryFn: () => adminApi.clubs.members(clubId),
	});
}

export function useClubMembersAdmin(params: ClubMembersListParams = {}) {
	return useQuery({
		queryKey: queryKeys.clubs.membersList(params),
		queryFn: () => adminApi.clubs.listMembers(params),
		placeholderData: keepPreviousData,
	});
}

export function useUpdateClubAdmin(id: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateClub) => adminApi.clubs.update(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
		},
	});
}

export function useUpdateClubSubscription(id: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateSubscription) => adminApi.clubs.updateSubscription(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
		},
	});
}

export function useUpdateClubBoost(id: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateBoost) => adminApi.clubs.updateBoost(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
		},
	});
}

export function useDeleteClub() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.clubs.delete(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all }),
	});
}

export function useRestoreClub() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.clubs.restore(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all }),
	});
}

export function useApproveClub() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.moderation.approveClub(id),
		onSuccess: (_, id) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.moderation.all });
		},
	});
}

export function useReindexClubs() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => adminApi.clubs.reindex(),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubs.indexStats,
			}),
	});
}

export function usePruneClubIndex() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => adminApi.clubs.pruneIndex(),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubs.indexStats,
			}),
	});
}

export function useClubIndexStats() {
	return useQuery({
		queryKey: queryKeys.clubs.indexStats,
		queryFn: () => adminApi.clubs.indexStats(),
	});
}

export function useRejectClub() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminApi.moderation.rejectClub(id),
		onSuccess: (_, id) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(id) });
			queryClient.invalidateQueries({ queryKey: queryKeys.moderation.all });
		},
	});
}
